import React from 'react';
import { Pressable, StyleSheet, Text, View, type PressableStateCallbackType } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../styles/theme';

interface Props {
  /** The axis name: « La base de tes repas », « Le temps que tu y mets »… */
  name: string;
  /** What Du Chef understood, e.g. « Protéine d'abord ». */
  current: string;
  /** The other reading of the same axis, e.g. « Végétal d'abord ». */
  opposite: string;
  onFlip: () => void;
  /** Phone: the name sits above the value row instead of beside it. */
  compact?: boolean;
  testID: string;
}

/**
 * One line of the profile read-back on the profile screen: the axis name,
 * the reading Du Chef made, and the opposite reading as a pill with a swap
 * icon. The whole row is the button; one tap flips the axis.
 */
export function AxisRow({ name, current, opposite, onFlip, compact, testID }: Props) {
  return (
    <Pressable
      onPress={onFlip}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`${name}: ${current}`}
      accessibilityHint={opposite}
      style={({ hovered }: PressableStateCallbackType & { hovered?: boolean }) => [styles.row, compact && styles.rowCompact, hovered && styles.rowHover]}
    >
      <Text style={[styles.name, !compact && styles.nameWide]} numberOfLines={1}>{name}</Text>
      <View style={styles.values}>
        <View style={styles.current}>
          <View style={styles.dot} />
          <Text style={styles.currentText} numberOfLines={2} testID={`${testID}-current`}>{current}</Text>
        </View>
        <View style={styles.alt}>
          <Feather name="repeat" size={12} color={Paprika.textSecondary} />
          <Text style={styles.altText} numberOfLines={2}>{opposite}</Text>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 12, paddingHorizontal: 14, borderRadius: 14,
    backgroundColor: Paprika.cardBg, borderWidth: 1, borderColor: Paprika.hairline,
    boxShadow: '0 6px 18px -10px rgba(82, 68, 60, 0.35)',
  },
  rowCompact: { flexDirection: 'column', alignItems: 'stretch', gap: 8 },
  rowHover: { borderColor: Paprika.accent },
  name: { fontFamily: 'Inter-SemiBold', fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', color: Paprika.textMuted },
  // StyleSheet drops `width: undefined`, so the column width lives in its own style.
  nameWide: { width: 112 },
  values: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
  current: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  dot: { width: 9, height: 9, borderRadius: 5, backgroundColor: Paprika.accent },
  currentText: { flex: 1, fontFamily: 'Inter-SemiBold', fontSize: 15, lineHeight: 20, color: Paprika.accent },
  alt: {
    flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 1,
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 999, backgroundColor: Paprika.bgSunk,
  },
  altText: { fontFamily: 'Inter-Medium', fontSize: 12.5, lineHeight: 16, color: Paprika.textSecondary, flexShrink: 1 },
});
